/**
 * Tracked share links — POST /api/v1/share-links (Backend/share_links_routes.py).
 *
 * Every share gets a per-sharer code: the server keeps one code per sharer
 * (the verified user, else this browser's visitor id) per target per surface,
 * and answers the target's public URL with `?s=<code>&utm_source=share&
 * utm_medium=<channel>&utm_campaign=<surface>_share`. Each POST also records
 * the share itself (copy / channel / native), so it is sent once per action.
 *
 * Browsers only let a copy or a share sheet happen inside the click that asked
 * for it, and a network round trip can outlast that. So the first link for a
 * target is cached for the tab: later shares build their URL from it at once
 * and the POST (which returns the same code) only records the event. The very
 * first one copies through a promised ClipboardItem where the browser has one.
 */

import { apiService } from '../../../services/apiService';
import { API_BASE, storedUserId, visitorIds } from './api';
import { copyText } from './shared';

export type ShareSurface = 'platform' | 'techpage' | 'unipage';
export type ShareChannel = 'copy' | 'linkedin' | 'whatsapp' | 'x' | 'email' | 'native';
export interface ShareTarget { target_kind: 'tech' | 'university'; target_id: string; surface: ShareSurface }
export interface ShareLink { code: string; url: string }

const CACHE_KEY = 'utech_share_links';
const memory = new Map<string, string>();

/** Who the link belongs to: the signed-in account, else this browser. */
const sharerKey = (): string => storedUserId() || visitorIds().visitor_id || 'anon';
const keyOf = (t: ShareTarget): string => `${t.surface}|${t.target_kind}|${t.target_id}|${sharerKey()}`;

const readStore = (): Record<string, string> => {
  try { return JSON.parse(sessionStorage.getItem(CACHE_KEY) || '{}') || {}; } catch { return {}; }
};

/** The cached tracked URL for this target (any channel's), or null. */
export const cachedShareUrl = (t: ShareTarget): string | null => {
  const k = keyOf(t);
  const hit = memory.get(k) || readStore()[k];
  return typeof hit === 'string' && hit ? hit : null;
};

const remember = (t: ShareTarget, url: string) => {
  const k = keyOf(t);
  memory.set(k, url);
  try {
    const all = readStore();
    all[k] = url;
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(all));
  } catch { /* storage off: the in-memory copy still serves this page */ }
};

/** The same tracked URL with this channel as utm_medium. */
export const urlForChannel = (url: string, channel: ShareChannel): string => {
  try {
    const u = new URL(url, typeof window !== 'undefined' ? window.location.origin : undefined);
    u.searchParams.set('utm_medium', channel);
    return u.toString();
  } catch {
    return /[?&]utm_medium=/.test(url)
      ? url.replace(/([?&]utm_medium=)[^&#]*/, `$1${channel}`)
      : `${url}${url.includes('?') ? '&' : '?'}utm_medium=${channel}`;
  }
};

/** Mint (or reuse) the sharer's code and record this share. null when the
 *  server could not (offline, rate-limited, not deployed yet). */
export async function createShareLink(t: ShareTarget, channel: ShareChannel): Promise<ShareLink | null> {
  const uid = storedUserId();
  let token = '';
  try { token = (uid && apiService.getStoredAccessToken?.()) || ''; } catch { token = ''; }
  const qs = uid ? `?user_id=${encodeURIComponent(uid)}` : '';
  try {
    const res = await fetch(`${API_BASE()}/api/v1/share-links${qs}`, {
      method: 'POST',
      mode: 'cors',
      credentials: 'omit',
      // The share menu may be closing, or a new tab opening, as this goes out.
      keepalive: true,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ ...t, channel, ...visitorIds() }),
    });
    if (!res.ok) return null;
    const body: any = await res.json().catch(() => ({}));
    const d = body?.data && typeof body.data === 'object' ? body.data : body;
    const code = typeof d?.code === 'string' ? d.code : '';
    const url = typeof d?.url === 'string' ? d.url : '';
    if (!code || !url) return null;
    remember(t, url);
    return { code, url };
  } catch {
    return null;
  }
}

/**
 * The tracked link for one share action. `now` is set when a code is cached
 * (use it inside the click); `ready` always resolves — to the fresh tracked
 * URL, else the cached one, else `fallback` (the plain public URL) — and is
 * the one POST that records this share.
 */
export const shareLinkFor = (t: ShareTarget, channel: ShareChannel, fallback: string): { now: string | null; ready: Promise<string> } => {
  const cached = cachedShareUrl(t);
  const ready = createShareLink(t, channel)
    .then(r => (r ? urlForChannel(r.url, channel) : cached ? urlForChannel(cached, channel) : fallback))
    .catch(() => (cached ? urlForChannel(cached, channel) : fallback));
  return { now: cached ? urlForChannel(cached, channel) : null, ready };
};

/** Copy text that is still on its way, keeping the click's permission: a
 *  ClipboardItem holding a promise where the browser supports one, else copy
 *  once it arrives. */
export async function copyWhenReady(text: Promise<string>): Promise<boolean> {
  try {
    const CI: any = typeof window !== 'undefined' ? (window as any).ClipboardItem : undefined;
    if (CI && navigator.clipboard?.write) {
      const blob = text.then(t => new Blob([t], { type: 'text/plain' }));
      await navigator.clipboard.write([new CI({ 'text/plain': blob })]);
      return true;
    }
  } catch { /* fall back below */ }
  return copyText(await text);
}

/** The share-channel URL for a link (LinkedIn, WhatsApp, X, e-mail). */
export const channelHref = (channel: ShareChannel, url: string, title: string): string => {
  const enc = encodeURIComponent;
  switch (channel) {
    case 'linkedin': return `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`;
    case 'whatsapp': return `https://wa.me/?text=${enc(`${title} ${url}`)}`;
    case 'x': return `https://twitter.com/intent/tweet?url=${enc(url)}&text=${enc(title)}`;
    case 'email': return `mailto:?subject=${enc(title)}&body=${enc(`${title}\n${url}`)}`;
    default: return url;
  }
};

/** `?s=<code>` on an opened page — a share code, if it looks like one. */
export const shareCodeFrom = (search: string): string | null => {
  try {
    const c = (new URLSearchParams(search).get('s') || '').trim();
    // share_links codes: 8 characters of lowercase base32.
    return /^[a-z2-7]{8}$/i.test(c) ? c.toLowerCase() : null;
  } catch { return null; }
};

const OPENED_KEY = 'utech_share_opened';
const opened = new Set<string>();

/** Whether this tab has already reported opening this share code (a reload,
 *  a preview switch or a filter change is not a new open). */
export const shareOpenReported = (code: string): boolean => {
  if (opened.has(code)) return true;
  try { return (sessionStorage.getItem(OPENED_KEY) || '').split(',').includes(code); } catch { return false; }
};

export const markShareOpenReported = (code: string): void => {
  opened.add(code);
  try {
    const list = (sessionStorage.getItem(OPENED_KEY) || '').split(',').filter(Boolean);
    if (!list.includes(code)) sessionStorage.setItem(OPENED_KEY, [...list, code].slice(-50).join(','));
  } catch { /* storage off */ }
};
